import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getAdminApiUrl, ENV_COLORS } from '@/pages/Admin/shared';
import {
  ANIMATION_STYLE,
  HeroStat,
  StatRow,
  SectionCard,
  CHART_PALETTE,
  TOOLTIP_STYLE,
  TOOLTIP_LABEL_STYLE,
  TOOLTIP_ITEM_STYLE,
  tooltipFormatter,
  EmptyChart,
  Icons,
  fmtInt,
  fmtNum,
  fmtDate,
  fmtRelative,
  dateToIsoStart,
  dateToIsoEnd,
} from '../shared';
import { DateRangeControl, defaultRange } from '@/components/shared';
import {
  useBusinessAnalytics,
  type BusinessAnalyticsRow,
} from '../api/useBusinessAnalytics';

// ── Charts ───────────────────────────────────────────────────────────────────
function NotificationFunnelChart({ row }: { row: BusinessAnalyticsRow }) {
  const data = [
    { stage: 'Sent', count: row.business_notification_deliveries_sent ?? 0, fill: '#a78bfa' },
    { stage: 'Delivered', count: row.business_notification_deliveries_delivered ?? 0, fill: '#38bdf8' },
    { stage: 'Opened', count: row.business_notification_deliveries_opened ?? 0, fill: '#34d399' },
  ];
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) return <EmptyChart message="No notifications sent in this period" height={210} />;

  return (
    <div className="ss-chart"><ResponsiveContainer width="100%" height={210}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 24, left: 10, bottom: 0 }}>
        <CartesianGrid stroke="#1f1f3a" strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" stroke="#52527a" tick={{ fontSize: 11, fill: '#7e7ea8' }} />
        <YAxis type="category" dataKey="stage" stroke="#52527a" tick={{ fontSize: 12, fill: '#c4c4e0' }} width={80} />
        <Tooltip
          cursor={{ fill: '#1f1f3a55' }}
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={tooltipFormatter}
        />
        <Bar dataKey="count" radius={[0, 6, 6, 0]} animationDuration={900} animationEasing="ease-out">
          {data.map((d, i) => (
            <Cell key={i} fill={d.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer></div>
  );
}

function DealSourceChart({ row }: { row: BusinessAnalyticsRow }) {
  const data = [
    { name: 'Business page', value: row.deal_views_from_business_page ?? 0 },
    { name: 'Notification', value: row.deal_views_from_notification ?? 0 },
  ];
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <EmptyChart message="No deal views in this period" height={210} />;
  const colors = ['#a78bfa', '#38bdf8'];

  return (
    <div className="ss-chart"><ResponsiveContainer width="100%" height={210}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          stroke="#0f0f1a"
          animationDuration={900}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={colors[i]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={tooltipFormatter}
        />
        <Legend wrapperStyle={{ fontSize: 12, color: '#c4c4e0' }} />
      </PieChart>
    </ResponsiveContainer></div>
  );
}

function EventInterestsChart({ row }: { row: BusinessAnalyticsRow }) {
  const data = [
    { name: 'Going', value: row.event_interests_going ?? 0 },
    { name: 'Interested', value: row.event_interests_interested ?? 0 },
  ];
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <EmptyChart message="No event interests in this period" height={210} />;
  const colors = ['#34d399', '#fbbf24'];

  return (
    <div className="ss-chart"><ResponsiveContainer width="100%" height={210}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          stroke="#0f0f1a"
          animationDuration={900}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={colors[i]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={tooltipFormatter}
        />
        <Legend wrapperStyle={{ fontSize: 12, color: '#c4c4e0' }} />
      </PieChart>
    </ResponsiveContainer></div>
  );
}

function CoverActionsChart({ row }: { row: BusinessAnalyticsRow }) {
  const data = [
    { action: 'Set', count: row.cover_history_set ?? 0 },
    { action: 'Updated', count: row.cover_history_updated ?? 0 },
    { action: 'Cleared', count: row.cover_history_cleared ?? 0 },
    { action: 'Expired', count: row.cover_history_expired ?? 0 },
  ];
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) return <EmptyChart message="No cover history in this period" height={210} />;

  return (
    <div className="ss-chart"><ResponsiveContainer width="100%" height={210}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="#1f1f3a" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="action" stroke="#52527a" tick={{ fontSize: 12, fill: '#c4c4e0' }} />
        <YAxis stroke="#52527a" tick={{ fontSize: 11, fill: '#7e7ea8' }} allowDecimals={false} />
        <Tooltip
          cursor={{ fill: '#1f1f3a55' }}
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={tooltipFormatter}
        />
        <Bar dataKey="count" radius={[6, 6, 0, 0]} animationDuration={900}>
          {data.map((_, i) => (
            <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer></div>
  );
}

function EngagementMixChart({ row }: { row: BusinessAnalyticsRow }) {
  const data = [
    { name: 'Check-ins', value: row.check_ins_total ?? 0 },
    { name: 'Live sessions', value: row.live_check_ins_started ?? 0 },
    { name: 'Favorites', value: row.favorites_total ?? 0 },
    { name: 'Going intents', value: row.going_intents_submitted ?? 0 },
    { name: 'Deal views', value: row.deal_views_total ?? 0 },
  ];
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <EmptyChart message="No engagement signals yet" height={240} />;

  return (
    <div className="ss-chart"><ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 24, left: 10, bottom: 0 }}>
        <CartesianGrid stroke="#1f1f3a" strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" stroke="#52527a" tick={{ fontSize: 11, fill: '#7e7ea8' }} />
        <YAxis type="category" dataKey="name" stroke="#52527a" tick={{ fontSize: 12, fill: '#c4c4e0' }} width={100} />
        <Tooltip
          cursor={{ fill: '#1f1f3a55' }}
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={tooltipFormatter}
        />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} animationDuration={1100} animationEasing="ease-out">
          {data.map((_, i) => (
            <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer></div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function BusinessDetailPage(): React.ReactElement {
  const { businessId } = useParams<{ businessId: string }>();
  const apiUrl = getAdminApiUrl('prod');

  const [[fromDate, toDate], setRange] = useState<[string, string]>(defaultRange);

  const queryParams = useMemo(() => {
    const params: { since?: string; until?: string } = {};
    if (fromDate) params.since = dateToIsoStart(fromDate);
    if (toDate) params.until = dateToIsoEnd(toDate);
    return params;
  }, [fromDate, toDate]);

  const { data, loading, error, refetch } = useBusinessAnalytics(apiUrl, queryParams);

  const business: BusinessAnalyticsRow | undefined = useMemo(
    () => data.businesses.find((b) => b.business_id === businessId),
    [data.businesses, businessId],
  );

  const dateLabel = useMemo(() => {
    if (!fromDate && !toDate) return 'All time';
    if (fromDate && toDate && fromDate === toDate) return fromDate;
    if (fromDate && toDate) return `${fromDate} → ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    return `Until ${toDate}`;
  }, [fromDate, toDate]);

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <style>{ANIMATION_STYLE}</style>
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-16">
        {/* Breadcrumb */}
        <div className="mb-6 flex items-center gap-2 text-sm">
          <Link to="/b2b" className="text-gray-500 hover:text-gray-300 transition-colors no-underline">
            B2B Dashboards
          </Link>
          <span className="text-gray-700">/</span>
          <Link to="/b2b/business-analytics" className="text-gray-500 hover:text-gray-300 transition-colors no-underline">
            Business Insights
          </Link>
          <span className="text-gray-700">/</span>
          <span className="text-gray-300">{business?.business_name ?? '…'}</span>
        </div>

        {/* Loading / not found */}
        {loading && !business && (
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-2xl p-12 text-center">
            <div className="inline-flex items-center gap-3 text-gray-400 text-sm">
              <svg className="w-4 h-4 animate-spin text-violet-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Loading business analytics…
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-950/30 border border-red-800/40 rounded-xl p-4 flex items-center justify-between">
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={refetch}
              className="px-3 py-1.5 text-sm font-medium text-red-300 bg-red-900/40 rounded-lg hover:bg-red-900/60 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !business && !error && (
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-2xl p-12 text-center">
            <p className="text-gray-300 text-base font-semibold">Business not found</p>
            <p className="text-gray-500 text-sm mt-1">
              The business may not exist in production or may be soft-deleted.
            </p>
            <Link
              to="/b2b/business-analytics"
              className="inline-block mt-4 px-4 py-2 text-sm font-medium text-violet-300 bg-violet-900/30 border border-violet-700/50 rounded-lg hover:bg-violet-900/50 transition-colors no-underline"
            >
              ← Back to Business Insights
            </Link>
          </div>
        )}

        {business && (
          <>
            {/* Hero */}
            <div className="ss-fade-up mb-6 bg-gradient-to-br from-violet-950/40 via-[#181831] to-[#14142a] border border-violet-800/30 rounded-2xl p-6 shadow-xl shadow-black/30 relative overflow-hidden">
              <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-violet-600/10 blur-3xl ss-shimmer" />
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-white font-display">{business.business_name}</h1>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                      PRODUCTION
                    </span>
                    {business.verified && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border bg-emerald-950/40 border-emerald-800/40 text-emerald-300 capitalize">
                        {business.verified}
                      </span>
                    )}
                    {business.business_type && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border bg-gray-800 border-gray-700 text-gray-300">
                        {business.business_type}
                      </span>
                    )}
                    {business.cover_currently_active && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border bg-amber-950/40 border-amber-800/40 text-amber-300">
                        Cover active{business.cover_current_amount ? ` · $${business.cover_current_amount}` : ''}
                      </span>
                    )}
                    {business.created_at && (
                      <span className="text-xs text-gray-500">
                        Onboarded {fmtDate(business.created_at)}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-xs text-gray-500 font-mono">{business.business_id}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-xs text-gray-500">Showing</span>
                  <span className="text-sm font-semibold text-violet-300">{dateLabel}</span>
                </div>
              </div>
            </div>

            {/* Date filter */}
            <div className="mb-6 flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3 bg-[#181831] border border-gray-800/80 rounded-xl">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Date Range</span>
              <DateRangeControl
                from={fromDate || null}
                to={toDate || null}
                onChange={(f, t) => setRange([f ?? '', t ?? ''])}
              />
            </div>

            {/* Key metrics — hero stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <HeroStat
                label="Total Check-ins"
                value={business.check_ins_total}
                sub={fmtRelative(business.last_check_in_at)
                  ? `Last: ${fmtRelative(business.last_check_in_at)}`
                  : 'No check-ins yet'}
                accent="violet"
                icon={Icons.pin}
                delayMs={0}
              />
              <HeroStat
                label="Unique Check-in Users"
                value={business.check_ins_distinct_users}
                sub={`${fmtInt(business.going_intents_submitted)} going intents`}
                accent="sky"
                icon={Icons.shield}
                delayMs={80}
              />
              <HeroStat
                label="Live Sessions"
                value={business.live_check_ins_started}
                sub={`${fmtInt(business.live_check_in_distinct_users)} unique users`}
                accent="emerald"
                icon={Icons.bolt}
                delayMs={160}
              />
              <HeroStat
                label="Favorites"
                value={business.favorites_total}
                sub={`${fmtInt(business.business_notifications_created)} notifications sent`}
                accent="amber"
                icon={Icons.heart}
                delayMs={240}
              />
            </div>

            {/* Visual breakdowns */}
            <div className="mb-6">
              <div className="ss-fade-in mb-3 flex items-center gap-2" style={{ animationDelay: '300ms' }}>
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-300/70">
                  Visual Breakdowns
                </span>
                <div className="flex-1 h-px bg-gradient-to-r from-violet-700/40 via-violet-700/10 to-transparent" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard
                  title="Engagement Mix"
                  description="All-source signal volume across this business"
                  icon={Icons.chart}
                  delayMs={320}
                >
                  <EngagementMixChart row={business} />
                </SectionCard>
                <SectionCard
                  title="Notification Funnel"
                  description="Send → Delivered → Opened pipeline"
                  icon={Icons.bell}
                  delayMs={380}
                >
                  <NotificationFunnelChart row={business} />
                </SectionCard>
                <SectionCard
                  title="Deal Views by Source"
                  description="Where users discovered deals"
                  icon={Icons.tag}
                  delayMs={440}
                >
                  <DealSourceChart row={business} />
                </SectionCard>
                <SectionCard
                  title="Event Interests"
                  description="Going vs Interested split"
                  icon={Icons.calendar}
                  delayMs={500}
                >
                  <EventInterestsChart row={business} />
                </SectionCard>
                <SectionCard
                  title="Cover History Actions"
                  description="Distribution of cover lifecycle events"
                  icon={Icons.dollar}
                  delayMs={560}
                  className="lg:col-span-2"
                >
                  <CoverActionsChart row={business} />
                </SectionCard>
              </div>
            </div>

            {/* Detailed grid */}
            <div className="ss-fade-in mb-3 flex items-center gap-2" style={{ animationDelay: '600ms' }}>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-300/70">
                Detailed Breakdown
              </span>
              <div className="flex-1 h-px bg-gradient-to-r from-violet-700/40 via-violet-700/10 to-transparent" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard
                title="Check-ins"
                description="Standard check-in activity at this venue"
                icon={Icons.pin}
                delayMs={620}
              >
                <StatRow label="Total check-ins" value={fmtInt(business.check_ins_total)} />
                <StatRow label="Unique check-in users" value={fmtInt(business.check_ins_distinct_users)} />
                <StatRow label="Going intents submitted" value={fmtInt(business.going_intents_submitted)} hint="Users who flagged intent to attend" />
                <StatRow label="Last check-in" value={fmtDate(business.last_check_in_at)} hint={fmtRelative(business.last_check_in_at)} />
              </SectionCard>

              <SectionCard
                title="Live Check-ins"
                description="Real-time session activity"
                icon={Icons.bolt}
                delayMs={680}
              >
                <StatRow label="Live sessions started" value={fmtInt(business.live_check_ins_started)} />
                <StatRow label="Sessions completed" value={fmtInt(business.live_check_ins_completed)} />
                <StatRow label="Active right now" value={fmtInt(business.live_check_ins_active_now)} />
                <StatRow label="Unique live users" value={fmtInt(business.live_check_in_distinct_users)} />
                <StatRow label="Avg session minutes" value={fmtNum(business.live_check_in_avg_minutes)} />
                <StatRow label="Max session minutes" value={fmtInt(business.live_check_in_max_minutes)} />
                <StatRow label="Last live session" value={fmtDate(business.last_live_check_in_at)} hint={fmtRelative(business.last_live_check_in_at)} />
              </SectionCard>

              <SectionCard
                title="Events"
                description="Hosted events and user interest"
                icon={Icons.calendar}
                delayMs={740}
              >
                <StatRow label="Events created" value={fmtInt(business.events_created)} />
                <StatRow label="Events completed" value={fmtInt(business.events_completed)} />
                <StatRow label="Events cancelled" value={fmtInt(business.events_cancelled)} />
                <StatRow label="Recurring events" value={fmtInt(business.events_recurring)} />
                <StatRow label="Going interests" value={fmtInt(business.event_interests_going)} />
                <StatRow label="Interested" value={fmtInt(business.event_interests_interested)} />
                <StatRow label="Interests removed" value={fmtInt(business.event_interests_removed)} />
                {business.event_analytics_rows > 0 && (
                  <>
                    <StatRow label="Event analytics rows" value={fmtInt(business.event_analytics_rows)} />
                    <StatRow label="Avg conversion %" value={fmtNum(business.avg_event_conversion_pct)} hint="Actual ÷ Planned attendees" />
                    <StatRow label="Total planned attendees" value={fmtInt(business.event_planned_attendees_total)} />
                    <StatRow label="Total actual attendees" value={fmtInt(business.event_actual_attendees_total)} />
                  </>
                )}
              </SectionCard>

              <SectionCard
                title="Deals"
                description="Promotion creation and engagement"
                icon={Icons.tag}
                delayMs={800}
              >
                <StatRow label="Deals created" value={fmtInt(business.deals_created)} />
                <StatRow label="Active now" value={fmtInt(business.deals_active_now)} />
                <StatRow label="Scheduled" value={fmtInt(business.deals_scheduled)} />
                <StatRow label="Expired" value={fmtInt(business.deals_expired)} />
                <StatRow label="Deleted" value={fmtInt(business.deals_deleted)} />
                <StatRow label="Total views" value={fmtInt(business.deal_views_total)} />
                <StatRow label="Distinct view users" value={fmtInt(business.deal_views_distinct_users)} />
                <StatRow label="Views from business page" value={fmtInt(business.deal_views_from_business_page)} />
                <StatRow label="Views from notification" value={fmtInt(business.deal_views_from_notification)} />
                <StatRow label="Notifications sent" value={fmtInt(business.deal_notifications_sent)} />
                <StatRow label="Notifications opened" value={fmtInt(business.deal_notifications_tapped)} />
                <StatRow label="Notifications failed" value={fmtInt(business.deal_notifications_failed)} />
              </SectionCard>

              <SectionCard
                title="Push Notifications"
                description="Direct push activity from this business"
                icon={Icons.bell}
                delayMs={860}
              >
                <StatRow label="Notifications created" value={fmtInt(business.business_notifications_created)} />
                <StatRow label="Sent rows" value={fmtInt(business.business_notifications_sent_rows)} hint="Notifications with status = sent" />
                <StatRow label="Total send attempts" value={fmtInt(business.business_notification_deliveries_sent)} />
                <StatRow label="Delivered" value={fmtInt(business.business_notification_deliveries_delivered)} />
                <StatRow label="Opened" value={fmtInt(business.business_notification_deliveries_opened)} />
                <StatRow label="Peak subscriber count" value={fmtInt(business.business_notification_max_subscriber_count)} hint="MAX recorded snapshot — current count pending DynamoDB integration" />
                <StatRow label="Weekly stats — sent" value={fmtInt(business.weekly_notifications_sent)} />
                <StatRow label="Weekly stats — opened" value={fmtInt(business.weekly_notifications_opened)} />
                <StatRow label="Attributed visits" value={fmtInt(business.weekly_attributed_visits)} hint="Check-ins attributed to a notification tap" />
              </SectionCard>

              <SectionCard
                title="Cover Charge"
                description="Live cover history and tap-throughs"
                icon={Icons.dollar}
                delayMs={920}
              >
                <StatRow label="Currently active" value={business.cover_currently_active ? 'Yes' : 'No'} />
                <StatRow label="Current amount" value={business.cover_current_amount ? `$${business.cover_current_amount}` : '—'} />
                <StatRow label="History entries" value={fmtInt(business.cover_history_entries)} />
                <StatRow label="Set" value={fmtInt(business.cover_history_set)} />
                <StatRow label="Updated" value={fmtInt(business.cover_history_updated)} />
                <StatRow label="Cleared" value={fmtInt(business.cover_history_cleared)} />
                <StatRow label="Expired" value={fmtInt(business.cover_history_expired)} />
                <StatRow label="Cover notifications sent" value={fmtInt(business.cover_notifications_sent)} />
                <StatRow label="Cover notifications opened" value={fmtInt(business.cover_notifications_tapped)} />
                <StatRow label="Cover notifications failed" value={fmtInt(business.cover_notifications_failed)} />
              </SectionCard>

              <SectionCard
                title="Weekly Performance"
                description="Latest pre-aggregated rollup data"
                icon={Icons.trend}
                delayMs={980}
              >
                <StatRow label="Weeks of summary data" value={fmtInt(business.weekly_summary_weeks)} />
                <StatRow label="Latest summary week" value={fmtDate(business.latest_summary_week)} />
                <StatRow label="Latest week visitors" value={fmtInt(business.latest_week_total_visitors)} />
                <StatRow label="Trend status" value={business.latest_week_trend_status ?? '—'} />
                <StatRow label="Growth %" value={fmtNum(business.latest_week_growth_rate_percent)} />
                <StatRow label="Avg session minutes" value={fmtNum(business.latest_week_avg_session_minutes)} />
                <StatRow label="Dominant crowd level" value={business.latest_dominant_crowd_level ?? '—'} />
                <StatRow label="Latest crowd week" value={fmtDate(business.latest_crowd_week)} />
              </SectionCard>

              <SectionCard
                title="Engagement & Audit"
                description="Favorites and admin activity"
                icon={Icons.shield}
                delayMs={1040}
              >
                <StatRow label="Favorites" value={fmtInt(business.favorites_total)} hint="Users who bookmarked this venue" />
                <StatRow label="Admin audit events" value={fmtInt(business.admin_audit_events)} hint="Manual admin actions targeting this business" />
              </SectionCard>
            </div>

            {/* Footer hint */}
            <p className="mt-6 text-center text-[11px] text-gray-600">
              Showing {dateLabel.toLowerCase()} totals · refresh to pull latest data
            </p>
          </>
        )}
      </div>
    </div>
  );
}
